import React, { useState } from "react";
import { api } from "../lib/api";
import { Share2, Copy, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareMyExperience — modal that lets a customer share booking details +
 * concierge trust card with a trusted contact via a tokenized link.
 *
 * TREL never persists the contact's phone/email — only the share token and a
 * free-text "recipient_name" the customer types (e.g., "Mom", "Alex").
 */
export default function ShareMyExperience({ booking, open, onClose }) {
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  // Mode is "referral" when shared from a Completed booking (Share My Concierge),
  // else "safety" (share-trip-with-trusted-contact). Defaults to safety so
  // existing callers don't change behavior.
  const isReferral = booking?._mode === "referral";
  const conciergeName = booking?.provider_user?.name || "your concierge";

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/share-experience", {
        booking_id: booking.booking_id,
        recipient_name: recipient,
        contact_method: "link",
      });
      const full = `${window.location.origin}${r.data.share_url}`;
      setShareUrl(full);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate share link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Copy failed — long-press the link to copy");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[#071A2D]/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      role="dialog"
      data-testid={isReferral ? "share-concierge-modal" : "share-experience-modal"}
    >
      <div className="bg-[#FFF8EE] rounded-3xl p-7 max-w-md w-full relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#071A2D]/40 hover:text-[#071A2D]"
          aria-label="Close"
          data-testid="share-close-btn"
        >
          <X size={18} />
        </button>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
          {isReferral ? "Referral · Share My Concierge" : "Safety · Share My Experience"}
        </div>
        <h2 className="font-serif text-2xl mt-2 flex items-center gap-2">
          <Share2 size={20} className="text-[#FF7A45]" />
          {isReferral ? `Recommend ${conciergeName}` : "Share with a trusted contact"}
        </h2>
        <p className="text-sm text-[#071A2D]/70 mt-2 leading-relaxed">
          {isReferral
            ? `Loved your experience? Share ${conciergeName}'s profile with friends. They'll see a trust card with rating, badges, and specialties — no personal details from your booking.`
            : "Share your booking details and concierge profile with someone you trust. They'll see the date, time, and your verified TREL Concierge."}
        </p>

        <div className="mt-5 p-4 rounded-2xl bg-white border border-[#071A2D]/10">
          <div className="font-medium text-sm">{booking?.experience?.title}</div>
          <div className="text-xs text-[#071A2D]/60 mt-1">
            {booking?.date} · {booking?.time} · {booking?.guests} guests
          </div>
          {booking?.provider_user?.name && (
            <div className="text-xs text-[#064E3B] mt-1">with {booking.provider_user.name}</div>
          )}
        </div>

        {!shareUrl && (
          <>
            <label className="block text-xs uppercase tracking-widest text-[#071A2D]/60 mt-5 mb-2">
              {isReferral ? "Who's this for? (optional)" : "Who's receiving this? (optional)"}
            </label>
            <input
              data-testid="share-recipient-input"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={isReferral ? "Sara, Alex, Mike…" : "Mom, Alex, Best friend…"}
              className="kbd-input"
            />
            <button
              onClick={generate}
              disabled={busy}
              data-testid="share-generate-btn"
              className="btn-coral w-full mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              {busy ? "Generating…" : (isReferral ? "Generate referral link" : "Generate share link")}
            </button>
          </>
        )}

        {shareUrl && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest text-[#071A2D]/60 mb-2">
              {isReferral ? "Your referral link" : "Your share link"}
            </div>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="kbd-input flex-1 text-xs"
                data-testid="share-url-input"
              />
              <button
                onClick={copy}
                className="rounded-full bg-[#064E3B] text-[#FFF8EE] px-4 text-sm inline-flex items-center gap-1.5 hover:brightness-110"
                data-testid="share-copy-btn"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="text-[11px] text-[#071A2D]/55 mt-3">
              {isReferral
                ? "Paste this in any messaging app. The link shows your Concierge's trust card — no personal contact info is shared with TREL."
                : "Paste this in any messaging app. The link shows your booking and concierge — no personal contact info is shared with TREL."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
